import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AtSign,
  Coffee,
  Mail,
  MapPin,
  Menu,
  Phone,
  Search,
  Shirt,
  ShoppingCart,
  Sparkles,
  X
} from "lucide-react";
import {
  business,
  gallery,
  instagramFeed,
  instagramProfile,
  policies,
  products,
  routes,
  storefront
} from "./config/storefront.js";
import { useCart } from "./hooks/useCart.js";
import { formatMoney } from "./lib/money.js";
import { normalizePath } from "./lib/routing.js";

const product = products[0];

function isCartQuery(search) {
  return new URLSearchParams(search).get("olsPage") === "cart";
}

export function App() {
  const [routePath, setRoutePath] = useState(() => normalizePath(window.location.pathname));
  const [pendingHash, setPendingHash] = useState(window.location.hash);
  const [menuOpen, setMenuOpen] = useState(false);
  const [instagramOpen, setInstagramOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(() => isCartQuery(window.location.search));
  const [searchOpen, setSearchOpen] = useState(false);
  const [toast, setToast] = useState("");
  const cart = useCart();

  const route = routes[routePath];
  const modalOpen = instagramOpen || cartOpen || searchOpen;

  const closeOverlays = useCallback(() => {
    setInstagramOpen(false);
    setCartOpen(false);
    setSearchOpen(false);
  }, []);

  const navigate = useCallback((href) => {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    window.history.pushState({}, "", `${url.pathname}${url.search}${url.hash}`);
    setRoutePath(normalizePath(url.pathname));
    setPendingHash(url.hash);
    setMenuOpen(false);
    closeOverlays();
    if (isCartQuery(url.search)) setCartOpen(true);
  }, [closeOverlays]);

  const showToast = useCallback((message) => {
    setToast(message);
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => setToast(""), 2600);
  }, []);

  const handleAddToCart = useCallback((productId, quantity = 1) => {
    const selectedProduct = products.find((item) => item.id === productId);
    cart.addToCart(productId, quantity);
    if (selectedProduct) showToast(`${selectedProduct.name} added to cart.`);
  }, [cart, showToast]);

  useEffect(() => {
    const handlePopState = () => {
      setRoutePath(normalizePath(window.location.pathname));
      setPendingHash(window.location.hash);
      setMenuOpen(false);
      closeOverlays();
      if (isCartQuery(window.location.search)) setCartOpen(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [closeOverlays]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        closeOverlays();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeOverlays]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", menuOpen);
    document.body.classList.toggle("modal-open", modalOpen);
    return () => {
      document.body.classList.remove("menu-open", "modal-open");
    };
  }, [menuOpen, modalOpen]);

  useEffect(() => {
    document.title = route?.title || "Page Not Found | Anzivino's";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", metaDescription(route?.page));
  }, [route]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      if (!pendingHash) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      document.querySelector(pendingHash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [routePath, pendingHash]);

  return (
    <>
      <Announcement />
      <Header
        cartQuantity={cart.quantity}
        menuOpen={menuOpen}
        navigate={navigate}
        onInstagram={() => setInstagramOpen(true)}
        onMenu={() => setMenuOpen((open) => !open)}
        onCart={() => setCartOpen(true)}
        onSearch={() => setSearchOpen(true)}
        routePath={routePath}
      />
      <main className="main" id="main">
        <PageSwitch
          page={route?.page}
          navigate={navigate}
          onAddToCart={handleAddToCart}
          onInstagram={() => setInstagramOpen(true)}
          showToast={showToast}
        />
      </main>
      <NewsletterSection showToast={showToast} />
      <Footer navigate={navigate} />
      <InstagramModal isOpen={instagramOpen} onClose={() => setInstagramOpen(false)} />
      <CartDrawer
        cart={cart}
        isOpen={cartOpen}
        navigate={navigate}
        onClear={cart.clearCart}
        onClose={() => setCartOpen(false)}
      />
      <SearchModal isOpen={searchOpen} navigate={navigate} onClose={() => setSearchOpen(false)} />
      <div className={`toast ${toast ? "is-visible" : ""}`} role="status" aria-live="polite">
        {toast}
      </div>
    </>
  );
}

function Announcement() {
  return <div className="announcement">{storefront.announcement}</div>;
}

function Header({
  cartQuantity,
  menuOpen,
  navigate,
  onCart,
  onInstagram,
  onMenu,
  onSearch,
  routePath
}) {
  return (
    <header className="site-header">
      <div className="nav-shell">
        <nav className="nav-links" aria-label="Primary navigation">
          <NavLink href="/" label="Home" navigate={navigate} routePath={routePath} />
          <NavLink href="/shop/" label="Shop" navigate={navigate} routePath={routePath} />
          <NavLink href="/about/" label="About" navigate={navigate} routePath={routePath} />
          <NavLink href="/policy/" label="Policy" navigate={navigate} routePath={routePath} />
        </nav>
        <button className="icon-btn menu-toggle js-menu-toggle" type="button" aria-label="Open menu" onClick={onMenu}>
          <Menu aria-hidden="true" size={20} />
        </button>
        <SiteLink className="brand" href="/" navigate={navigate} aria-label="Anzivino's home">
          <img src="/assets/images/anzivinos-logo.jpg" alt="Anzivino's Sweets & Boutique" />
        </SiteLink>
        <div className="nav-actions">
          <button className="icon-btn hide-small js-open-search" type="button" aria-label="Search" onClick={onSearch}>
            <Search aria-hidden="true" size={19} />
          </button>
          <button
            className="icon-btn hide-small js-open-instagram"
            type="button"
            aria-label="Open Instagram feed"
            onClick={onInstagram}
          >
            <AtSign aria-hidden="true" size={19} />
          </button>
          <button className="icon-btn js-open-cart" type="button" aria-label="Open cart" onClick={onCart}>
            <ShoppingCart aria-hidden="true" size={19} />
            <span className="cart-count js-cart-count">{cartQuantity}</span>
          </button>
        </div>
      </div>
      <nav className={`mobile-menu js-mobile-menu ${menuOpen ? "is-open" : ""}`} aria-label="Mobile navigation">
        <NavLink href="/" label="Home" navigate={navigate} routePath={routePath} />
        <NavLink href="/shop/" label="Shop" navigate={navigate} routePath={routePath} />
        <NavLink href="/about/" label="About" navigate={navigate} routePath={routePath} />
        <NavLink href="/policy/" label="Policy" navigate={navigate} routePath={routePath} />
        <NavLink href="/sign-up/" label="Sign Up" navigate={navigate} routePath={routePath} />
      </nav>
    </header>
  );
}

function NavLink({ href, label, navigate, routePath }) {
  const current = routePath === href || (href !== "/" && routePath.startsWith(href));
  return (
    <SiteLink className="nav-link" href={href} navigate={navigate} aria-current={current ? "page" : undefined}>
      {label}
    </SiteLink>
  );
}

function SiteLink({ href, navigate, onClick, children, ...props }) {
  const handleClick = (event) => {
    onClick?.(event);
    if (event.defaultPrevented || !isInternalHref(href)) return;
    event.preventDefault();
    navigate(href);
  };
  return (
    <a href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  );
}

function isInternalHref(href) {
  return href.startsWith("/") || href.startsWith("#");
}

function PageSwitch({ page, navigate, onAddToCart, onInstagram, showToast }) {
  switch (page) {
    case "home":
      return <HomePage navigate={navigate} onAddToCart={onAddToCart} onInstagram={onInstagram} />;
    case "shop":
      return <ShopPage navigate={navigate} onAddToCart={onAddToCart} />;
    case "product":
      return <ProductPage onAddToCart={onAddToCart} />;
    case "about":
      return <AboutPage navigate={navigate} onInstagram={onInstagram} />;
    case "policy":
      return <PolicyPage type="policy" />;
    case "privacy":
      return <PolicyPage type="privacy" />;
    case "terms":
      return <PolicyPage type="terms" />;
    case "signup":
      return <SignupPage showToast={showToast} />;
    case "account":
      return <AccountRoutePage type="account" navigate={navigate} />;
    case "createAccount":
      return <AccountRoutePage type="createAccount" navigate={navigate} />;
    case "orders":
      return <AccountRoutePage type="orders" navigate={navigate} />;
    case "login":
      return <AccountRoutePage type="login" navigate={navigate} />;
    default:
      return <NotFoundPage navigate={navigate} />;
  }
}

function HomePage({ navigate, onAddToCart, onInstagram }) {
  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="hero-eyebrow">Ellicottville, New York</span>
            <h1>{business.name}</h1>
            <div className="category-line">{business.categories.join(" • ")}</div>
            <p>{business.bio}, serving sweet treats, boutique finds, and coffee from a pink storefront on Washington Street.</p>
            <div className="hero-actions">
              <SiteLink className="btn primary" href="/shop/" navigate={navigate}>Shop Coffee</SiteLink>
              <SiteLink className="btn secondary" href="#visit" navigate={navigate}>Visit Us</SiteLink>
              <button className="btn secondary js-open-instagram" type="button" onClick={onInstagram}>
                View Instagram
              </button>
            </div>
          </div>
          <figure className="hero-art" aria-label="Anzivino's sweets and boutique hero image">
            <img
              src="/assets/images/hero-sweets-boutique.jpg"
              alt="Ice cream taco, dipped cone, coffee, and boutique details in a pink sweet shop"
            />
          </figure>
        </div>
      </section>
      <QuickStrip navigate={navigate} onInstagram={onInstagram} />
      <ProductFeature navigate={navigate} onAddToCart={onAddToCart} />
      <StorySection />
      <GallerySection navigate={navigate} />
      <InstagramBand onInstagram={onInstagram} />
      <VisitSection />
    </>
  );
}

function QuickStrip({ navigate, onInstagram }) {
  return (
    <div className="quick-strip" aria-label="Highlights">
      <div className="quick-strip-grid">
        <SiteLink className="quick-card" href="/about/" navigate={navigate}>
          <img src="/assets/images/about-sign-upright.jpg" alt="Anzivino's pink storefront" />
          <span><strong>Visit Ellicottville</strong>{business.address}</span>
        </SiteLink>
        <SiteLink className="quick-card product-quick-card" href="/shop/" navigate={navigate}>
          <img src={product.image} alt="Coffee bag" />
          <span><strong>Featured product</strong>Specialty coffee grounds</span>
        </SiteLink>
        <button className="quick-card js-open-instagram" type="button" onClick={onInstagram}>
          <img src="/assets/images/about-cone.jpg" alt="Ice cream cone" />
          <span><strong>Follow along</strong>{instagramProfile.handle} feed</span>
        </button>
      </div>
    </div>
  );
}

function ProductFeature({ navigate, onAddToCart }) {
  return (
    <section className="section">
      <div className="section-inner product-feature">
        <div className="media-frame product-image product-image-feature">
          <img src={product.image} alt={product.name} />
        </div>
        <div className="product-copy">
          <h2>{product.name}</h2>
          <p>{product.description}</p>
          <TagList tags={product.tags} />
          <p className="price">{formatMoney(product.priceCents, product.currency)}</p>
          <div className="button-row">
            <SiteLink className="btn primary" href={product.path} navigate={navigate}>View Product</SiteLink>
            <button className="btn secondary js-add-cart" type="button" onClick={() => onAddToCart(product.id)}>
              Add to Cart
            </button>
          </div>
          <p className="storefront-note">{storefront.taxPolicy}</p>
        </div>
      </div>
    </section>
  );
}

function StorySection() {
  return (
    <section className="section cream">
      <div className="section-inner story-grid">
        <div className="story-copy">
          <h2>A Sweet Stop in Ellicottville</h2>
          <p>Take a trip to the storefront and enjoy ice cream, warm specialty drinks, apparel, and sweet treats in a bright pink boutique built for browsing.</p>
          <div className="story-points">
            <StoryPoint icon={<Sparkles />} title="Home of the first ice cream taco" body="A playful signature treat built for color, crunch, and a little bit of theater." />
            <StoryPoint icon={<Shirt />} title="Boutique style" body="Seasonal apparel, accessories, and cozy finds alongside the sweet shop counter." />
            <StoryPoint icon={<Coffee />} title="Coffee and warm drinks" body="Specialty drinks, coffee blend, and small indulgences for every season." />
          </div>
        </div>
        <div className="story-media">
          <div className="story-image"><img src="/assets/images/storefront-sign.jpg" alt="Anzivino's storefront sign" /></div>
          <div className="story-image"><img src="/assets/images/boutique-coats.jpg" alt="Boutique coats and apparel" /></div>
        </div>
      </div>
    </section>
  );
}

function StoryPoint({ icon, title, body }) {
  return (
    <div className="story-point">
      <span className="feature-icon">{icon}</span>
      <span><strong>{title}</strong><br />{body}</span>
    </div>
  );
}

function GallerySection({ navigate }) {
  return (
    <section className="section">
      <div className="section-inner">
        <div className="section-head">
          <div>
            <h2>From Our Shop</h2>
            <p>Rounded, photo-forward moments from the live site assets and Anzivino's social presence.</p>
          </div>
          <SiteLink className="btn secondary" href="/about/" navigate={navigate}>More About Us</SiteLink>
        </div>
        <div className="gallery-grid">
          {gallery.map((item) => <GalleryCard item={item} key={item.image} />)}
        </div>
      </div>
    </section>
  );
}

function GalleryCard({ item }) {
  return (
    <figure className="gallery-card">
      <img src={item.image} alt={item.title} style={{ objectPosition: item.position || "center" }} />
      <figcaption>{item.title}</figcaption>
    </figure>
  );
}

function InstagramBand({ onInstagram }) {
  return (
    <section className="section blush">
      <div className="section-inner instagram-band">
        <div>
          <h2>Follow Along<br />{instagramProfile.handle}</h2>
          <p>See what's new in the shop, sweet treats, behind the scenes moments, and cart announcements.</p>
          <button className="btn primary js-open-instagram" type="button" onClick={onInstagram}>View Instagram</button>
        </div>
        <div className="instagram-preview" aria-hidden="true">
          {instagramFeed.slice(0, 5).map((item) => (
            <img src={item.image} alt="" key={item.image} style={{ objectPosition: item.position || "center" }} />
          ))}
        </div>
      </div>
    </section>
  );
}

function VisitSection() {
  return (
    <section className="section compact" id="visit">
      <div className="section-inner">
        <div className="section-head">
          <div>
            <h2>Come Say Hi</h2>
            <p>Feel free to visit during normal business hours in Ellicottville, New York.</p>
          </div>
          <a className="btn secondary" href={business.maps.link} target="_blank" rel="noopener noreferrer">Open in Maps</a>
        </div>
        <div className="visit-grid">
          <div className="contact-panel">
            <ContactLine icon={<MapPin />} label={business.addressFull} href={business.maps.link} external />
            <ContactLine icon={<Phone />} label={business.phone} href={business.phoneHref} />
            <ContactLine icon={<Mail />} label={business.email} href={business.emailHref} />
            <ContactLine icon={<AtSign />} label={instagramProfile.handle} href={business.social.instagram} external />
          </div>
          <div className="contact-panel">
            <h3>Hours</h3>
            <HoursList />
          </div>
          <div className="map-frame">
            <iframe title="Map to Anzivino's Sweets & Boutique" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src={business.maps.embed} />
            <div className="map-overlay">
              <strong>{business.shortName}</strong>
              <span>{business.address}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactLine({ external = false, href, icon, label }) {
  return (
    <a className="contact-line" href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>
      <span className="mini-icon">{icon}</span>
      <span>{label}</span>
    </a>
  );
}

function HoursList() {
  return (
    <dl className="hours">
      {business.hours.map(([day, hours]) => (
        <div key={day}>
          <dt>{day}</dt>
          <dd>{hours}</dd>
        </div>
      ))}
    </dl>
  );
}

function ShopPage({ navigate, onAddToCart }) {
  return (
    <>
      <PageHero title="Shop" intro="Featured products from Anzivino's Sweets & Boutique, starting with the house specialty coffee blend." />
      <section className="section">
        <div className="section-inner">
          <div className="section-head">
            <div>
              <h2>Featured Products</h2>
              <p>Order online or visit the storefront for ice cream, apparel, coffee, and sweets.</p>
            </div>
          </div>
          <div className="shop-grid">
            {products.map((item) => <ProductCard product={item} navigate={navigate} onAddToCart={onAddToCart} key={item.id} />)}
          </div>
        </div>
      </section>
    </>
  );
}

function ProductCard({ navigate, onAddToCart, product: item }) {
  return (
    <article className="product-card">
      <SiteLink className="product-image product-image-link" href={item.path} navigate={navigate}>
        <img src={item.image} alt={item.name} />
      </SiteLink>
      <div className="product-card-body">
        <h3>{item.name}</h3>
        <p>{item.description}</p>
        <strong className="price">{formatMoney(item.priceCents, item.currency)}</strong>
        <p className="storefront-note">SKU {item.sku}</p>
        <div className="product-actions">
          <SiteLink className="btn secondary" href={item.path} navigate={navigate}>Details</SiteLink>
          <button className="btn primary js-add-cart" type="button" onClick={() => onAddToCart(item.id)}>
            Add to Cart
          </button>
        </div>
      </div>
    </article>
  );
}

function ProductPage({ onAddToCart }) {
  const [quantity, setQuantity] = useState(1);
  const stepQuantity = (step) => setQuantity((value) => Math.max(1, Math.min(12, value + step)));

  return (
    <section className="section">
      <div className="section-inner product-detail">
        <div className="media-frame product-image product-image-large">
          <img src={product.image} alt={product.name} />
        </div>
        <div className="product-copy">
          <h1>{product.name}</h1>
          <p>{product.description}</p>
          <TagList tags={product.tags} />
          <p className="price">{formatMoney(product.priceCents, product.currency)}</p>
          <dl className="storefront-config-list">
            <div><dt>SKU</dt><dd>{product.sku}</dd></div>
            <div><dt>Availability</dt><dd>Contact storefront for current availability</dd></div>
            <div><dt>Checkout</dt><dd>Direct request via {business.email}</dd></div>
          </dl>
          <div className="button-row">
            <label className="quantity" aria-label="Quantity">
              <button type="button" onClick={() => stepQuantity(-1)} aria-label="Decrease quantity">-</button>
              <input type="number" value={quantity} min="1" max="12" onChange={(event) => setQuantity(Math.max(1, Math.min(12, Number(event.target.value) || 1)))} />
              <button type="button" onClick={() => stepQuantity(1)} aria-label="Increase quantity">+</button>
            </label>
            <button className="btn primary js-add-cart" type="button" onClick={() => onAddToCart(product.id, quantity)}>
              Add to Cart
            </button>
          </div>
          <p>Prefer to shop in person? Visit {business.address} during regular hours.</p>
        </div>
      </div>
    </section>
  );
}

function AboutPage({ navigate, onInstagram }) {
  return (
    <>
      <PageHero title="About Us" intro="Come say hi at the storefront in Ellicottville, NY and enjoy ice cream, sweet treats, coffee, and boutique finds." />
      <section className="section">
        <div className="section-inner about-feature">
          <img src="/assets/images/grand-opening-storefront.jpg" alt="Anzivino's grand opening storefront" />
          <div className="story-copy">
            <h2>Come Say Hi!</h2>
            <p>Take a trip to our storefront location in Ellicottville, NY and enjoy our ice cream and other sweet treats.</p>
            <p>Warm up by the fireplace with specialty drinks, shop apparel and accessories, and follow the latest treats on Instagram.</p>
            <div className="button-row">
              <button className="btn primary js-open-instagram" type="button" onClick={onInstagram}>Open Instagram Feed</button>
              <SiteLink className="btn secondary" href="/about/#visit" navigate={navigate}>Plan a Visit</SiteLink>
            </div>
          </div>
        </div>
      </section>
      <GallerySection navigate={navigate} />
      <VisitSection />
    </>
  );
}

function PolicyPage({ type }) {
  const content = {
    policy: {
      title: "Policy",
      intro: "Return, refund, and customer care information from the current Anzivino's policy route.",
      cards: [
        ["Return and Refund Policy", policies.returnPolicy],
        ["Questions", [`Call ${business.phone} or email ${business.email}.`, `Visit the storefront at ${business.addressFull}.`]]
      ]
    },
    privacy: {
      title: "Privacy Policy",
      intro: "The current site lists privacy policy copy as coming soon. This rebuild keeps that state visible and gives visitors a direct contact path.",
      cards: [["Privacy Policy Coming Soon", policies.privacy]]
    },
    terms: {
      title: "Terms and Conditions",
      intro: "The current terms route is a placeholder. This page preserves the route and customer contact details for staging.",
      cards: [["Terms Coming Soon", policies.terms]]
    }
  }[type];

  return (
    <section className="section blush">
      <div className="legal-page">
        <h1>{content.title}</h1>
        <p>{content.intro}</p>
        {content.cards.map(([heading, lines]) => (
          <article className="legal-card" key={heading}>
            <h2>{heading}</h2>
            <ul>{lines.map((line) => <li key={line}>{line}</li>)}</ul>
          </article>
        ))}
      </div>
    </section>
  );
}

function SignupPage({ showToast }) {
  return (
    <section className="section">
      <div className="section-inner sign-up-panel">
        <img src="/assets/images/newsletter-cart.jpg" alt="Anzivino's cart booking graphic" />
        <div>
          <h1>Be the First to Know!</h1>
          <p>Get {storefront.newsletterOffer} when you sign up for our newsletter.</p>
          <NewsletterForm id="signup-email" showToast={showToast} />
        </div>
      </div>
    </section>
  );
}

function AccountRoutePage({ navigate, type }) {
  const content = {
    account: {
      title: "Account",
      intro: "No sign-in is required for the current storefront. Customers can request checkout directly and the shop will follow up personally.",
      primary: ["Request Checkout", `${business.emailHref}?subject=Anzivino's%20order%20request`],
      secondary: ["Shop Products", "/shop/"]
    },
    createAccount: {
      title: "Create Account",
      intro: `Customer accounts are optional for this simpler storefront. For now, join the list for ${storefront.newsletterOffer} and order directly from the shop.`,
      primary: ["Join the List", "/sign-up/"],
      secondary: ["Contact Us", business.emailHref]
    },
    orders: {
      title: "Orders",
      intro: `Orders are handled directly so the shop can keep costs simple. For current order questions, contact ${business.email} or call ${business.phone}.`,
      primary: ["Email Order Help", `${business.emailHref}?subject=Anzivino's%20order%20question`],
      secondary: ["Store Policy", "/policy/"]
    },
    login: {
      title: "Sign In",
      intro: "Sign-in is not required for this storefront. Customers can still request checkout directly from the cart.",
      primary: ["Open Cart", "/shop/?olsPage=cart"],
      secondary: ["Create Account", "/m/create-account/"]
    }
  }[type];

  return (
    <section className="section blush">
      <div className="legal-page account-page">
        <h1>{content.title}</h1>
        <p>{content.intro}</p>
        <article className="legal-card account-card">
          <h2>Simple Storefront</h2>
          <dl className="storefront-config-list">
            <div><dt>Checkout</dt><dd>{storefront.checkoutMode.replace("_", " ")}</dd></div>
            <div><dt>Order contact</dt><dd>{business.email}</dd></div>
            <div><dt>Catalog</dt><dd>{products.length} active {products.length === 1 ? "product" : "products"}</dd></div>
          </dl>
          <div className="account-actions">
            <MaybeInternalLink className="btn primary" href={content.primary[1]} navigate={navigate}>{content.primary[0]}</MaybeInternalLink>
            <MaybeInternalLink className="btn secondary" href={content.secondary[1]} navigate={navigate}>{content.secondary[0]}</MaybeInternalLink>
          </div>
        </article>
      </div>
    </section>
  );
}

function MaybeInternalLink({ href, navigate, children, ...props }) {
  return isInternalHref(href)
    ? <SiteLink href={href} navigate={navigate} {...props}>{children}</SiteLink>
    : <a href={href} {...props}>{children}</a>;
}

function NotFoundPage({ navigate }) {
  return (
    <section className="section blush">
      <div className="legal-page">
        <h1>Page Not Found</h1>
        <p>This route is not part of the crawled Anzivino's staging surface.</p>
        <SiteLink className="btn primary" href="/" navigate={navigate}>Back Home</SiteLink>
      </div>
    </section>
  );
}

function PageHero({ intro, title }) {
  return (
    <section className="page-hero">
      <div className="section-inner">
        <h1>{title}</h1>
        <p>{intro}</p>
      </div>
    </section>
  );
}

function TagList({ tags }) {
  return <div className="tag-list">{tags.map((tag) => <span className="tag" key={tag}>{tag}</span>)}</div>;
}

function NewsletterSection({ showToast }) {
  return (
    <section className="section compact newsletter">
      <div className="newsletter-inner">
        <div>
          <h2>Be the First to Know!</h2>
          <p>Exclusive offers, new flavors, and shop updates delivered straight to your inbox.</p>
        </div>
        <NewsletterForm id="footer-email" showToast={showToast} />
      </div>
    </section>
  );
}

function NewsletterForm({ id, showToast }) {
  const [note, setNote] = useState("");
  const handleSubmit = (event) => {
    event.preventDefault();
    setNote("Thanks! You're on the list.");
    event.currentTarget.reset();
    showToast("Thanks for signing up.");
  };

  return (
    <form className="inline-form js-newsletter-form" onSubmit={handleSubmit}>
      <label className="sr-only" htmlFor={id}>Email address</label>
      <input className="input" id={id} name="email" type="email" placeholder="Email address" required />
      <button className="btn primary" type="submit">Sign Up</button>
      <p className="form-note js-form-note">{note}</p>
    </form>
  );
}

function Footer({ navigate }) {
  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div>
          <img className="footer-logo" src="/assets/images/anzivinos-logo.jpg" alt="Anzivino's" />
          <p>{business.bio}</p>
          <div className="button-row">
            <a className="icon-btn" href={business.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><AtSign aria-hidden="true" size={18} /></a>
            <a className="icon-btn" href={business.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">f</a>
          </div>
        </div>
        <FooterList title="Shop" links={[["/shop/", "All Products"], ["/shop/", "Apparel"], [product.path, "Coffee"], ["/shop/", "Sweets"]]} navigate={navigate} />
        <FooterList title="About" links={[["/about/", "Our Story"], ["/about/", "Gallery"], ["/about/#visit", "Visit"]]} navigate={navigate} />
        <FooterList title="Customer Care" links={[["/policy/", "Policy"], ["/privacy-policy/", "Privacy Policy"], ["/terms-and-conditions/", "Terms & Conditions"], ["/m/account/", "Account"], ["/m/orders/", "Orders"], [business.emailHref, "Contact Us"]]} navigate={navigate} />
        <div>
          <h3>Visit</h3>
          <ul>
            <li><a href={business.maps.link} target="_blank" rel="noopener noreferrer">{business.address}</a></li>
            <li><a href={business.phoneHref}>{business.phone}</a></li>
            <li><a href={business.emailHref}>{business.email}</a></li>
          </ul>
        </div>
      </div>
      <div className="copyright">&copy; 2026 {business.name}. All rights reserved.</div>
    </footer>
  );
}

function FooterList({ links, navigate, title }) {
  return (
    <div>
      <h3>{title}</h3>
      <ul>
        {links.map(([href, label]) => (
          <li key={`${href}-${label}`}>
            {isInternalHref(href) ? <SiteLink href={href} navigate={navigate}>{label}</SiteLink> : <a href={href}>{label}</a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function InstagramModal({ isOpen, onClose }) {
  return (
    <div className={`modal-backdrop js-instagram-modal ${isOpen ? "is-open" : ""}`} role="presentation" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <section className="instagram-modal" role="dialog" aria-modal="true" aria-labelledby="instagram-title">
        <div className="modal-head">
          <h2 id="instagram-title">{instagramProfile.handle}</h2>
          <button className="close-btn js-close-modal" type="button" aria-label="Close Instagram feed" onClick={onClose}>
            <X aria-hidden="true" size={18} />
          </button>
        </div>
        <div className="instagram-profile">
          <img src="/assets/images/anzivinos-logo.jpg" alt="Anzivino's Instagram profile logo" />
          <div>
            <div className="ig-stats">
              <span><strong>{instagramProfile.posts}</strong>posts</span>
              <span><strong>{instagramProfile.followers}</strong>followers</span>
              <span><strong>{instagramProfile.following}</strong>following</span>
            </div>
            <strong>{instagramProfile.profile}</strong>
            <p>{instagramProfile.bio.map((line) => <span key={line}>{line}<br /></span>)}{business.address}</p>
          </div>
        </div>
        <div className="ig-feed-grid">
          {instagramFeed.map((item) => (
            <a href={business.social.instagram} target="_blank" rel="noopener noreferrer" key={item.image}>
              <img src={item.image} alt={item.title} style={{ objectPosition: item.position || "center" }} />
            </a>
          ))}
        </div>
        <div className="modal-actions">
          <a className="btn primary" href={business.social.instagram} target="_blank" rel="noopener noreferrer">View on Instagram</a>
        </div>
      </section>
    </div>
  );
}

function CartDrawer({ cart, isOpen, navigate, onClear, onClose }) {
  return (
    <div className={`drawer-backdrop js-cart-drawer ${isOpen ? "is-open" : ""}`} role="presentation" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="cart-drawer" role="dialog" aria-modal="true" aria-labelledby="cart-title">
        <div className="drawer-head">
          <h2 id="cart-title">Cart</h2>
          <button className="close-btn js-close-drawer" type="button" aria-label="Close cart" onClick={onClose}>
            <X aria-hidden="true" size={18} />
          </button>
        </div>
        <div className="drawer-body js-cart-body">
          {cart.lineItems.length ? (
            <>
              {cart.lineItems.map((item) => <CartLine item={item} key={item.product.id} />)}
              <div className="cart-total"><span>Total</span><span>{formatMoney(cart.totalCents)}</span></div>
              <a className="btn primary" href={`${business.emailHref}?subject=Anzivino's%20order%20request`}>Request Checkout</a>
              <button className="btn secondary js-clear-cart" type="button" onClick={onClear}>Clear Cart</button>
            </>
          ) : (
            <>
              <p>Your cart is empty.</p>
              <SiteLink className="btn primary" href="/shop/" navigate={navigate}>Shop Featured Products</SiteLink>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function CartLine({ item }) {
  return (
    <div className="cart-line">
      <img src={item.product.image} alt={item.product.name} />
      <div>
        <h3>{item.product.name}</h3>
        <p>{formatMoney(item.product.priceCents)} x {item.quantity}</p>
      </div>
      <strong className="line-total">{formatMoney(item.lineTotalCents)}</strong>
    </div>
  );
}

function SearchModal({ isOpen, navigate, onClose }) {
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return searchItems();
    return searchItems().filter((item) => `${item.title} ${item.body}`.toLowerCase().includes(needle));
  }, [query]);

  const handleNavigate = (href) => {
    navigate(href);
    onClose();
  };

  return (
    <div className={`search-backdrop js-search-modal ${isOpen ? "is-open" : ""}`} role="presentation" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <section className="search-modal" role="dialog" aria-modal="true" aria-labelledby="search-title">
        <div className="search-head">
          <h2 id="search-title">Search Anzivino's</h2>
          <button className="close-btn js-close-search" type="button" aria-label="Close search" onClick={onClose}>
            <X aria-hidden="true" size={18} />
          </button>
        </div>
        <div className="search-body">
          <label className="sr-only" htmlFor="site-search">Search</label>
          <input className="input js-search-input" id="site-search" type="search" placeholder="Search products, policy, hours..." value={query} onChange={(event) => setQuery(event.target.value)} />
          <div className="search-results js-search-results">
            {results.length ? (
              results.map((item) => (
                <button className="search-result-button" type="button" key={item.href} onClick={() => handleNavigate(item.href)}>
                  <strong>{item.title}</strong><br /><span>{item.body}</span>
                </button>
              ))
            ) : (
              <p>No results yet.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function searchItems() {
  return [
    { title: "Home", body: "Ice cream, apparel, coffee, first ice cream taco", href: "/" },
    { title: product.name, body: `${product.tags.join(" ")} coffee ${formatMoney(product.priceCents)}`, href: product.path },
    { title: "About Us", body: "Storefront Ellicottville sweet treats warm specialty drinks", href: "/about/" },
    { title: "Policy", body: "Return refund 30 days unworn unused returns", href: "/policy/" },
    { title: "Visit", body: `${business.address} hours phone map`, href: "/about/#visit" },
    { title: "Account", body: "Login orders account checkout status", href: "/m/account/" },
    { title: "Sign Up", body: `${storefront.newsletterOffer} newsletter`, href: "/sign-up/" }
  ];
}

function metaDescription(page) {
  const copy = {
    home: "Ice cream, apparel, coffee, and sweet treats at Anzivino's Sweets & Boutique in Ellicottville, New York.",
    shop: "Shop Anzivino's featured specialty coffee blend and boutique sweets.",
    product: "Specialty Coffee Blend- GROUNDS from Anzivino's Sweets & Boutique.",
    about: "About Anzivino's Sweets & Boutique in Ellicottville, New York.",
    policy: "Return and refund policy for Anzivino's Sweets & Boutique.",
    privacy: "Privacy policy information for Anzivino's Sweets & Boutique.",
    terms: "Terms and conditions information for Anzivino's Sweets & Boutique.",
    signup: "Join the Anzivino's newsletter for 10% off your first purchase.",
    account: "Customer account and checkout status for Anzivino's Sweets & Boutique.",
    createAccount: "Customer account setup information for Anzivino's Sweets & Boutique.",
    orders: "Order support information for Anzivino's Sweets & Boutique.",
    login: "Sign-in status for Anzivino's Sweets & Boutique."
  };
  return copy[page] || copy.home;
}
